import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import BulkCreateRegistryEntitiesFlowClass from 'bulk-import/BulkCreateRegistryEntitiesFlow';
import {openBulkCreateTrialSamplesFlow} from 'bulk-import/BulkCreateTrialSamplesFlow';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  FolderCreateDropdownEnhancedProps,
  FolderCreateDropdownProps,
} from 'file-browser/FolderCreateDropdown';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  CREATE_MENU_COMPLEX_POLYMERS_QUERY,
  getComplexPolymerSchemaConfigs,
  getComplexPolymerSchemaConfiguration,
  isComplexPolymerSchema,
} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {
  openBulkReimportProteins,
  openBulkReimportSequences,
  openBulkUpdateBioentitiesModal,
  openBulkUpdateMixtureIngredientsModal,
} from 'nav/CreateDropdownActions';
import NavStore from 'nav/NavStore';
import useTagSchemaAwareObjectCreateQuery from 'nav/sidepanel/listing/hooks/useTagSchemaAwareObjectCreateQuery';
import CreateAndRegisterBioentityModal from 'registry/create-bioentity/CreateAndRegisterBioentityModal';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {toModelTypeConstant} from 'tag-schema/util/ModelTypeConversions';
import {
  isDNAOligoSchemaGraphQL,
  isDNASequenceSchemaGraphQL,
  isRNAOligoSchemaGraphQL,
  isRNASequenceSchema,
} from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {MenuHeight, MenuWidth, OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import sortedBy from 'util/sortedBy';

import {TagSchemaListingGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

const REQUIRED_PERMISSIONS = ['objectsCreatable', 'objectsRegisterable'] as const;

const BULK_ACTION_VALUES = {
  IMPORT_ENTITIES: 'bulk:importEntities',
  UPDATE_ENTITIES: 'bulk:updateEntities',
  IMPORT_TRIAL_SAMPLES: 'bulk:importTrialSamples',
  IMPORT_MIXTURE_INGREDIENTS: 'bulk:importMixtureIngredients',
  REIMPORT_DNA_RNA: 'bulk:reimportDnaRna',
  REIMPORT_AA: 'bulk:reimportAa',
} as const;

const useStyles = createStyles((theme) => ({
  dividerAbove: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
}));

function LoadingIconButton({
  isLoading,
  dropLeft,
  icon,
}: {
  isLoading?: boolean;
  dropLeft: boolean;
  icon: BenchlingIconName;
}): JSX.Element {
  return (
    <Tooltip text="Loading..." isDisabled={!isLoading} disableContentSelection={true}>
      <Button
        buttonStyle={dropLeft ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_BLUE}
        icon={icon}
        size={ButtonSize.SMALL}
        text={dropLeft ? 'Create' : null}
        disabled={isLoading}
      />
    </Tooltip>
  );
}

export function RegistryCreateMenu(
  props: FolderCreateDropdownProps & FolderCreateDropdownEnhancedProps
): JSX.Element {
  const complexPolymersSchemasResult = useSkippableBenchlingQuery<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >(CREATE_MENU_COMPLEX_POLYMERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
  });

  const allComplexPolymerSchemaConfigs = getComplexPolymerSchemaConfigs(complexPolymersSchemasResult);
  const {classes} = useStyles();

  const renderOptionContentFn = getRenderStarredOptionContent(
    props.toggleTagSchemaStar,
    props.starredSchemaIds,
    {getId: (optionData: OptionData) => optionData.value as string}
  );

  function createAndRegisterBioentity(tagSchema: TagSchemaListingGraphQL): void {
    const complexPolymerSchemaConfig = getComplexPolymerSchemaConfiguration(
      tagSchema.id,
      allComplexPolymerSchemaConfigs
    );
    legacyRunModal(CreateAndRegisterBioentityModal, {
      createAndRegisterBioentityArgs: {
        ...CreateBioentityUtil.getDefaultCreateAndRegisterBioentityArgs(),
        tagSchemaId: tagSchema.id,
        complexPolymerSchemaConfig,
      },
    });
  }

  function buildBulkActionOptions(): {
    options: Array<OptionData>;
    handlers: Record<OptionValue, () => void>;
  } {
    const options: Array<OptionData> = [];
    const handlers: Record<OptionValue, () => void> = {};
    const addBulkActionOption = (enabled: boolean, option: OptionData, onSelect: () => void): void => {
      if (enabled) {
        handlers[option.value] = onSelect;
        // Bulk actions are not tag schemas, so they should never render a star icon.
        options.push({...option, extra: {unstarrable: true}});
      }
    };

    const isRegistryImportEnabled = isFeatureEnabled({feature: RegistryFeature.REGISTRY_IMPORT});
    const isRegistryEditEnabled = isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT});
    const isRegistryMixturesEnabled = isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES});

    addBulkActionOption(
      isRegistryImportEnabled,
      {
        icon: 'table',
        name: 'Import entities',
        value: BULK_ACTION_VALUES.IMPORT_ENTITIES,
        dataTestAttrs: {component: 'RegistryMenu', element: 'dropdownItem', key: 'importEntities'},
      },
      () => {
        BulkCreateRegistryEntitiesFlowClass.open({
          initialRegistry: NavStore.getSource(props.folder.source!.id),
          complexPolymerSchemaConfigurations: allComplexPolymerSchemaConfigs,
        });
      }
    );
    addBulkActionOption(
      isRegistryEditEnabled,
      {icon: 'table', name: 'Update entities', value: BULK_ACTION_VALUES.UPDATE_ENTITIES},
      () => {
        openBulkUpdateBioentitiesModal(props.folder, allComplexPolymerSchemaConfigs);
      }
    );
    addBulkActionOption(
      isRegistryImportEnabled && BenchlingConfig.REGISTRY_ENABLE_SAMPLE_INGESTION,
      {
        icon: 'table',
        name: 'Import trial samples',
        value: BULK_ACTION_VALUES.IMPORT_TRIAL_SAMPLES,
        dataTestAttrs: {component: 'RegistryMenu', element: 'dropdownItem', key: 'importTrialSamples'},
      },
      () => {
        openBulkCreateTrialSamplesFlow({initialRegistry: NavStore.getSource(props.folder.source!.id)});
      }
    );
    addBulkActionOption(
      isRegistryMixturesEnabled && isRegistryEditEnabled,
      {
        icon: 'table',
        name: 'Import mixture ingredients',
        value: BULK_ACTION_VALUES.IMPORT_MIXTURE_INGREDIENTS,
      },
      () => {
        openBulkUpdateMixtureIngredientsModal(props.folder);
      }
    );
    addBulkActionOption(
      isRegistryEditEnabled,
      {icon: 'import', name: 'Reimport DNA/RNA sequences', value: BULK_ACTION_VALUES.REIMPORT_DNA_RNA},
      () => {
        openBulkReimportSequences(props.folder.id);
      }
    );
    addBulkActionOption(
      isRegistryEditEnabled,
      {icon: 'import', name: 'Reimport AA sequences', value: BULK_ACTION_VALUES.REIMPORT_AA},
      () => {
        openBulkReimportProteins(props.folder.id);
      }
    );

    return {options, handlers};
  }

  function buildSchemaCategoryOptions(): {
    options: Array<OptionData>;
    handlers: Record<OptionValue, () => void>;
  } {
    const options: Array<OptionData> = [];
    const handlers: Record<OptionValue, () => void> = {};

    if (!isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE})) {
      return {options, handlers};
    }

    const categoryDefs: Array<{
      name: string;
      folderItemType: string;
      predicate?: (tagSchema: TagSchemaListingGraphQL) => boolean;
      icon?: BenchlingIconName;
    }> = [
      {name: 'AA sequence', folderItemType: ModelTypeConstants.PROTEIN},
      {
        name: 'DNA Sequence',
        folderItemType: ModelTypeConstants.SEQUENCE,
        predicate: isDNASequenceSchemaGraphQL,
      },
      {
        name: 'RNA Sequence',
        folderItemType: ModelTypeConstants.SEQUENCE,
        predicate: isRNASequenceSchema,
        icon: 'rna-sequence',
      },
      {name: 'DNA oligo', folderItemType: ModelTypeConstants.DNA_OLIGO, predicate: isDNAOligoSchemaGraphQL},
      {name: 'RNA oligo', folderItemType: ModelTypeConstants.DNA_OLIGO, predicate: isRNAOligoSchemaGraphQL},
    ];

    if (BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES) {
      categoryDefs.push({
        name: 'Oligo duplex or oligo conjugate',
        folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
        predicate: (tagSchema: TagSchemaListingGraphQL) =>
          isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id),
        icon: 'network-wired',
      });
    }

    categoryDefs.push({
      name: 'Custom entity',
      folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
      predicate: (tagSchema: TagSchemaListingGraphQL) =>
        toModelTypeConstant(tagSchema.folderItemType) === ModelTypeConstants.BASIC_FOLDER_ITEM &&
        !isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id),
    });

    if (isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES})) {
      categoryDefs.push({name: 'Mixture', folderItemType: ModelTypeConstants.MIXTURE});
    }

    if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      categoryDefs.push({name: 'Molecule', folderItemType: ModelTypeConstants.MOLECULE});
    }

    for (const def of categoryDefs) {
      const schemas = props.tagSchemas?.filter(
        (tagSchema) =>
          (def.predicate ? def.predicate(tagSchema) : tagSchema.folderItemType === def.folderItemType) &&
          REQUIRED_PERMISSIONS.every((permission) => tagSchema.permissions[permission])
      );

      if (!props.starredSchemaIds || !schemas || schemas.length === 0) {
        continue;
      }

      const sortedSchemas = sortedBy(schemas, [
        (schema) => (props.starredSchemaIds!.includes(schema.id) ? 0 : 1),
      ]);

      const submenuOptions: Array<OptionData> = sortedSchemas.map((schema) => {
        handlers[schema.id] = () => {
          createAndRegisterBioentity(schema);
        };
        return {
          className: 'schema-option',
          icon: schema.iconId as BenchlingIconName,
          name: schema.name,
          value: schema.id,
          dataTestAttrs: {component: 'RegistryCreateMenu', element: 'createSchemaSubMenuItem'},
        };
      });

      options.push({
        icon: def.icon ?? (def.folderItemType as BenchlingIconName),
        name: def.name,
        value: `category:${def.folderItemType}:${def.name}`,
        submenu: [submenuOptions],
        dataTestAttrs: {component: 'RegistryCreateMenu', element: 'createSchemaMenuItem'},
      });
    }

    return {options, handlers};
  }

  const bulkActions = buildBulkActionOptions();
  const schemaCategories = buildSchemaCategoryOptions();
  // When both groups are present, draw a divider line above the first schema category to separate it
  // from the bulk actions above.
  const schemaOptions =
    bulkActions.options.length > 0 && schemaCategories.options.length > 0
      ? schemaCategories.options.map((option, index) =>
          index === 0 ? {...option, className: classes.dividerAbove} : option
        )
      : schemaCategories.options;
  const allOptions = [...bulkActions.options, ...schemaOptions];
  const allHandlers = {...bulkActions.handlers, ...schemaCategories.handlers};

  const isLoading = complexPolymersSchemasResult.type === QueryResultType.PENDING;

  return (
    <div {...getTestProps({component: 'RegistryCreateMenu', element: 'dropdown'})}>
      <Dropdown
        menu={
          <SearchableMenu
            optionDatas={allOptions}
            renderOptionContent={renderOptionContentFn}
            width={MenuWidth.MEDIUM}
            height={MenuHeight.MEDIUM}
            dropLeft={props.dropLeft}
            onSelect={(value: OptionValue) => {
              // Category/submenu-parent rows have no handler; selecting them is a no-op.
              allHandlers[value]?.();
            }}
          />
        }
      >
        <div>
          <Tooltip
            text="Create..."
            placement={FloatingPlacement.RIGHT}
            disableContentSelection={true}
            isDisabled={isLoading}
          >
            <LoadingIconButton isLoading={isLoading} dropLeft={props.dropLeft} icon="compose" />
          </Tooltip>
        </div>
      </Dropdown>
    </div>
  );
}

export default function QueryingRegistryCreateMenu(
  props: Omit<FolderCreateDropdownProps, 'onOpenFolder'>
): JSX.Element | null {
  const {folder, ...restProps} = props;
  const result = useTagSchemaAwareObjectCreateQuery(folder);

  if (result == null) {
    return <LoadingIconButton isLoading={true} dropLeft={props.dropLeft} icon="compose" />;
  } else {
    const {tagSchemas, starredTagSchemaIds, handleToggleTagSchemaStar, folder: modifiedFolder} = result;

    return (
      <RegistryCreateMenu
        {...restProps}
        starredSchemaIds={starredTagSchemaIds}
        toggleTagSchemaStar={handleToggleTagSchemaStar}
        tagSchemas={tagSchemas}
        onOpenFolder={noop}
        folder={modifiedFolder}
      />
    );
  }
}
